import React, { useEffect, useState } from 'react';
import './Auth.css';
import { getHubSpotContext } from '../utils/hubspotContext';

function Auth({ onAuthenticated }) {
  const [authWindow, setAuthWindow] = useState(null);
  const [authCheckInterval, setAuthCheckInterval] = useState(null);
  const [error, setError] = useState(null);
  const [showPopupBlocked, setShowPopupBlocked] = useState(false);

  // Auto-redirect to oauth-launcher if not authenticated
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const errorParam = params.get('error');

    if (token) {
      localStorage.setItem('slack_user_token', token);
      window.history.replaceState({}, document.title, '/');
      onAuthenticated(token);
    } else if (errorParam) {
      console.error('OAuth error:', errorParam);
      setError(errorParam);
      window.history.replaceState({}, document.title, '/');
    } else {
      // Check if already have token in localStorage before redirecting
      const existingToken = localStorage.getItem('slack_user_token');
      if (existingToken) {
        // Already have token, let useAuth handle it
        console.log('Token found in localStorage, waiting for auth check...');
        return;
      }

      // Auto-redirect to oauth-launcher
      const hubspotContext = getHubSpotContext();
      const hubspotUserId = hubspotContext?.userId;
      const hubspotPortalId = hubspotContext?.portalId;

      let oauthLauncherUrl = '/oauth-launcher.html';
      const urlParams = new URLSearchParams();
      if (hubspotUserId) {
        urlParams.append('hubspotUserId', hubspotUserId);
      }
      if (hubspotPortalId) {
        urlParams.append('hubspotPortalId', hubspotPortalId);
      }
      if (urlParams.toString()) {
        oauthLauncherUrl += '?' + urlParams.toString();
      }

      console.log('Auto-redirecting to OAuth launcher:', oauthLauncherUrl);
      window.location.href = oauthLauncherUrl;
    }

    // Listen for messages from popup window
    const handleAuthMessage = (event) => {
      // Only process Slack auth messages
      if (!event.data || !event.data.type) {
        return;
      }

      if (event.data.type === 'SLACK_AUTH_SUCCESS') {
        console.log('Slack authentication successful', event.data);
        const token = event.data.token || event.data.user?.token;

        if (token) {
          localStorage.setItem('slack_user_token', token);
          onAuthenticated(token);
        }

        // Close popup if still open
        if (authWindow && !authWindow.closed) {
          authWindow.close();
        }

        // Clear interval
        if (authCheckInterval) {
          clearInterval(authCheckInterval);
        }
      } else if (event.data.type === 'SLACK_AUTH_ERROR') {
        console.error('Slack authentication failed:', event.data.error);
        setError(event.data.error);

        if (authCheckInterval) {
          clearInterval(authCheckInterval);
        }
      }
    };

    window.addEventListener('message', handleAuthMessage);

    return () => {
      window.removeEventListener('message', handleAuthMessage);
      if (authCheckInterval) {
        clearInterval(authCheckInterval);
      }
    };
  }, [onAuthenticated, authWindow, authCheckInterval]);

  const initiateOAuth = () => {
    setError(null);
    setShowPopupBlocked(false);

    // Get HubSpot context to pass to OAuth
    const hubspotContext = getHubSpotContext();
    const hubspotUserId = hubspotContext?.userId;
    const hubspotPortalId = hubspotContext?.portalId;

    // Build oauth-launcher URL with HubSpot context
    let oauthLauncherUrl = '/oauth-launcher.html';
    const params = new URLSearchParams();
    if (hubspotUserId) {
      params.append('hubspotUserId', hubspotUserId);
    }
    if (hubspotPortalId) {
      params.append('hubspotPortalId', hubspotPortalId);
    }
    if (params.toString()) {
      oauthLauncherUrl += '?' + params.toString();
    }

    console.log('Redirecting to OAuth launcher:', oauthLauncherUrl);

    // Redirect to oauth-launcher page which handles the OAuth flow
    window.location.href = oauthLauncherUrl;
  };

  const checkAuthStatus = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
      const hubspotContext = getHubSpotContext();
      const hubspotUserId = hubspotContext?.userId;

      const headers = {};
      if (hubspotUserId) {
        headers['x-hubspot-user-id'] = hubspotUserId;
      }

      const url = hubspotUserId
        ? `${backendUrl}/api/auth/status?hubspotUserId=${hubspotUserId}`
        : `${backendUrl}/api/auth/status`;

      const response = await fetch(url, {
        credentials: 'include',
        headers
      });
      const data = await response.json();

      if (data.authenticated && data.token) {
        localStorage.setItem('slack_user_token', data.token);

        // Clear the polling interval
        if (authCheckInterval) {
          clearInterval(authCheckInterval);
          setAuthCheckInterval(null);
        }

        onAuthenticated(data.token);
      }
    } catch (error) {
      console.error('Failed to check auth status:', error);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Slack Extension</h1>
        <p>Sign in with your Slack account to continue</p>

        {error && (
          <div className="auth-error">
            <p>Authentication failed: {error}</p>
          </div>
        )}

        {showPopupBlocked && (
          <div className="auth-warning">
            <h4>Popup Blocked</h4>
            <p>Please allow popups for this site to authenticate with Slack.</p>
          </div>
        )}

        <button
          className="slack-signin-button"
          onClick={initiateOAuth}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.86957 12.1739C7.86957 13.0725 7.1337 13.8084 6.23478 13.8084C5.33587 13.8084 4.6 13.0725 4.6 12.1739C4.6 11.275 5.33587 10.5391 6.23478 10.5391H7.86957V12.1739Z" fill="#E01E5A"/>
            <path d="M8.68695 12.1739C8.68695 11.275 9.42282 10.5391 10.3217 10.5391C11.2207 10.5391 11.9565 11.275 11.9565 12.1739V13.8087C11.9565 14.7076 11.2207 15.4435 10.3217 15.4435C9.42282 15.4435 8.68695 14.7076 8.68695 13.8087V12.1739Z" fill="#E01E5A"/>
            <path d="M10.3217 7.43045C9.42282 7.43045 8.68695 6.69458 8.68695 5.79567C8.68695 4.89676 9.42282 4.16089 10.3217 4.16089C11.2207 4.16089 11.9565 4.89676 11.9565 5.79567V7.43045H10.3217Z" fill="#36C5F0"/>
            <path d="M10.3217 8.24785C11.2207 8.24785 11.9565 8.98372 11.9565 9.88263C11.9565 10.7815 11.2207 11.5174 10.3217 11.5174H8.68695V9.88263C8.68695 8.98372 9.42282 8.24785 10.3217 8.24785Z" fill="#36C5F0"/>
            <path d="M15.2261 9.88263C15.2261 8.98372 15.962 8.24785 16.8609 8.24785C17.7598 8.24785 18.4957 8.98372 18.4957 9.88263C18.4957 10.7815 17.7598 11.5174 16.8609 11.5174H15.2261V9.88263Z" fill="#2EB67D"/>
            <path d="M14.4087 9.88263C14.4087 10.7815 13.6728 11.5174 12.7739 11.5174C11.875 11.5174 11.1391 10.7815 11.1391 9.88263V8.24785C11.1391 7.34893 11.875 6.61307 12.7739 6.61307C13.6728 6.61307 14.4087 7.34893 14.4087 8.24785V9.88263Z" fill="#2EB67D"/>
            <path d="M12.7739 14.6261C13.6728 14.6261 14.4087 15.362 14.4087 16.2609C14.4087 17.1598 13.6728 17.8957 12.7739 17.8957C11.875 17.8957 11.1391 17.1598 11.1391 16.2609V14.6261H12.7739Z" fill="#ECB22E"/>
            <path d="M12.7739 13.8087C11.875 13.8087 11.1391 13.0728 11.1391 12.1739C11.1391 11.275 11.875 10.5391 12.7739 10.5391H14.4087V12.1739C14.4087 13.0728 13.6728 13.8087 12.7739 13.8087Z" fill="#ECB22E"/>
          </svg>
          Sign in with Slack
        </button>
      </div>
    </div>
  );
}

export default Auth;